/**
 * Pointer and keyboard input: drag-to-orbit, the wheel/pinch zoom, WASD
 * ground movement and the Escape/h-key handling.
 *
 * Reads the closure it was cut out of through `InputDeps`.
 */

import * as THREE from 'three';

import type { AtlasLive, AtlasState } from '../atlas';
import type { InspectorHandle } from './inspector';
import type { PickHandle } from './pick';
import type { FocusRigHandle } from './focusRig';
import type { CameraRigHandle } from './cameraRig';
import type { MountHandle } from './mount';
import type { InjectsHandle } from './injects';
import type { SelectionHandle } from './selection';
import type { OverlayViewHandle } from './overlayView';
import type { HexViewHandle } from './hexView';
import type { NavigationHandle } from './navigation';
import type { SiteChipsHandle } from './panels/siteChips';
import type { RoverDriveHandle } from './roverDrive';
import type { WorldHostState } from './worldHost';
import { TEST_SWITCHES } from '../urlFlags.ts';

export interface InputDeps {
  readonly focusRig: FocusRigHandle;
  readonly inspector: InspectorHandle;
  readonly cameraRig: CameraRigHandle;
  readonly own: (fn: () => void) => void;
  readonly pick: PickHandle;
  readonly state: AtlasState;
  readonly mount: MountHandle;
  readonly injects: InjectsHandle;
  readonly selection: SelectionHandle;
  readonly overlayView: OverlayViewHandle;
  readonly hexViewSeam: HexViewHandle;
  readonly navigation: NavigationHandle;
  readonly siteChips: SiteChipsHandle;
  readonly roverDrive: RoverDriveHandle;
  readonly worldHostState: WorldHostState;
  /** Written from inside this seam as well as read. */
  readonly live: AtlasLive;
  /** A SEASON or SOL slider is previewing a date its Escape would cancel. */
  readonly sliderPreviewing: boolean;
}

export interface InputHandle {
  /** Called from the tick's own WASD branch. */
  applyMovement(dt: number): void;
  readonly isDragging: boolean;
  /** The rover's own manual drive reads this alongside the WASD branch above,
   *  and a body switch or a cruise transition clears it directly. */
  readonly held: Set<string>;
  moveInput(): boolean;
  dragInvertYaw: boolean;
  yawInertia: number;
  pitchInertia: number;
  readonly INERTIA_DECAY: number;
}

export function createInput(deps: InputDeps): InputHandle {
  const { inspector, own, pick, state } = deps;
  const { focusCam } = deps.focusRig;
  const { orbit, orbitViewBy, WALK_BODY_R_M, stopCinema, surfaceRegime } = deps.cameraRig;
  const { surface } = deps.mount;
  const { walkFrozen } = deps.injects.state;
  const { applySelection } = deps.selection;
  const { renderOverlay } = deps.overlayView;
  const { setHexesEnabled } = deps.hexViewSeam;
  const { syncTierToAltitude, zoomOut } = deps.navigation;

  // Drag-to-orbit the camera around Mars (T1+). Drag horizontal = yaw,
  // vertical = pitch (polar tilt). Distinguishes drag from click via an
  // 8px movement threshold. Release inertia is computed from the *instant*
  // angular velocity at the moment of release (last frame's delta over its
  // dt), then decays fast so the player doesn't have to wait to click.
  let isDragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragMoved = 0;
  let lastMoveMs = 0;
  let lastYawVelRadSec = 0;
  let lastPitchVelRadSec = 0;
  let yawInertia = 0;        // rad/sec at moment of release
  let pitchInertia = 0;
  // Sensitivity: ~0.4° per px (smaller than the previous 0.57°/px — Erik
  // said it spun too fast).
  const YAW_PER_PX   = 0.007;
  const PITCH_PER_PX = 0.006;
  const INERTIA_DECAY = 7.0;          // exp(-dt * 7) — half-life ~100 ms
  const INERTIA_RELEASE_FACTOR = 0.25; // keep only 1/4 of the release velocity
  /** `descent-dojo --drag --inject invert-yaw`: the pre-fix horizontal sign. */
  let dragInvertYaw = false;

  surface.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    stopCinema();
    isDragging = true;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    dragMoved = 0;
    yawInertia = 0;
    pitchInertia = 0;
    lastMoveMs = performance.now();
    lastYawVelRadSec = 0;
    lastPitchVelRadSec = 0;
    surface.style.cursor = 'grabbing';
  });
  function onMouseMove(e: MouseEvent): void {
    if (!isDragging) return;
    const dx = e.clientX - dragStartX;
    const dy = e.clientY - dragStartY;
    dragMoved = Math.max(dragMoved, Math.abs(dx), Math.abs(dy));
    const now = performance.now();
    const dtSec = Math.max(0.001, (now - lastMoveMs) / 1000);
    const yawRad = dx * YAW_PER_PX;
    const pitchRad = dy * PITCH_PER_PX;
    // Positive, to match pitch's own sense: a ground point under the pointer
    // follows the pointer on both axes, not just the vertical one. The
    // velocity below stays negated - the inertia tick's own sign is built to
    // expect that, and flipping both here would reverse the coast on release.
    orbitViewBy(dragInvertYaw ? -yawRad : yawRad, pitchRad);
    deps.live.cameraDirty = true;
    lastYawVelRadSec = -yawRad / dtSec;
    lastPitchVelRadSec = pitchRad / dtSec;
    lastMoveMs = now;
    deps.live.needsRender = true;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
  }
  function onMouseUp(): void {
    if (!isDragging) return;
    isDragging = false;
    surface.style.cursor = 'grab';
    // The pointer has not moved but what is under it has, all through the drag.
    pick.hoverDirty = true;
    yawInertia = lastYawVelRadSec * INERTIA_RELEASE_FACTOR;
    pitchInertia = lastPitchVelRadSec * INERTIA_RELEASE_FACTOR;
  }
  // On the window rather than the canvas so a drag that leaves the canvas
  // still tracks; each of these used to outlive the mount it belonged to.
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
  own(() => {
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  });
  surface.addEventListener('dblclick', (e) => {
    pick.pickAt(e);
  });
  // One click, not two, answers the site finder's PICK ON GLOBE; a drag is still a drag.
  surface.addEventListener('click', (e) => {
    if (dragMoved < 3) pick.pickSitesAt(e);
  });
  surface.addEventListener('contextmenu', (e) => { e.preventDefault(); zoomOut(); });

  // Wheel / trackpad pinch → zoom. Multiplicative so the feel is consistent
  // at any current distance. Clamped per tier so you can't punch through
  // the surface or fly off into the void. Ctrl+wheel is the trackpad pinch
  // gesture in most browsers — handled uniformly with regular scroll.
  surface.addEventListener('wheel', (e) => {
    e.preventDefault();
    // A wheel notch moves the ground under a still cursor, and at a rung
    // change it moves the grid the cell was read at.
    pick.hoverDirty = true;
    stopCinema();
    const pixels = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    const notches = THREE.MathUtils.clamp(pixels / 100, -2, 2);
    if (deps.injects.state.ZOOM_KEEPS_PITCH) focusCam.pinPitch();
    focusCam.zoom(notches > 0 && deps.injects.state.ZOOM_OUT_SHORT ? notches * 0.8 : notches);
    deps.live.cameraDirty = true;
    deps.live.needsRender = true;
    syncTierToAltitude();
    // `syncTierToAltitude` early-returns within a tier, so the card's ALTITUDE,
    // GROUND SAMPLE and BAND rows would otherwise hold the tier's first reading
    // for the whole zoom - measured 22.1 km held across 12 notches of CLAIM.
    // Marked rather than painted: a trackpad flick issues wheel events several
    // times a frame and a repaint is two innerHTML rebuilds.
    if (!deps.injects.state.legacyCard) deps.overlayView.state.overlayDirty = true;
  }, { passive: false });
  // ── WASD ────────────────────────────────────────────────────────────────
  //
  // The orbit rig is right for looking AT a plot and wrong for being IN one.
  // Dragging swings you round a fixed point, so the only way to reach a
  // particular boulder was to zoom out, rotate, and zoom back in on it. These
  // move the point itself.
  //
  // W/S run along the camera's own heading and A/D across it, both flattened
  // into the ground plane — pressing forward while looking down should walk you
  // toward what you are looking at, not fly you into it. R/F raise and lower,
  // and Shift is a sprint.
  //
  // Speed is proportional to the orbit distance, which is what makes one set of
  // keys serve a 2 km hex and a patch of ground three metres across: the world
  // moves at a constant fraction of the frame per second, so it always feels
  // the same regardless of scale.
  const held = new Set<string>();
  const MOVE_KEYS = new Set(['w', 'a', 's', 'd', 'r', 'f']);

  function moveInput(): boolean {
    return state.tier >= 5 && [...MOVE_KEYS].some((k) => held.has(k));
  }

  /**
   * `descent-dojo --mover --inject mover-sign`: `?moversign=old` restores the
   * pre-fix forward sign, so the control can be armed from the URL a fresh
   * page loads rather than reaching into state after the fact.
   */
  const moverSignOld = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('moversign') === 'old';
  /** `atlas-clock-check.mjs --inject slider-keys`: `?sliderholdskeys=1`, a focused slider swallowing every key. */
  const sliderHoldsEveryKey = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('sliderholdskeys') === '1';
  const RANGE_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown']);

  function applyMovement(dt: number): void {
    if (walkFrozen || !moveInput()) return;
    const sprint = held.has('shift') ? 3.0 : 1.0;
    // 0.9 of the orbit distance per second at full tilt.
    const step = orbit.distance * 0.9 * sprint * Math.min(dt, 0.05);
    const sx = Math.sin(focusCam.state.yaw);
    const cx = Math.cos(focusCam.state.yaw);
    // The plot frame's +z is south (see plotEye), so the camera's own
    // heading into the scene, flattened, is (-sin yaw, +cos yaw).
    let fx = -sx;
    let fz = moverSignOld ? -cx : cx;
    let mx = 0;
    let mz = 0;
    if (held.has('w')) { mx += fx; mz += fz; }
    if (held.has('s')) { mx -= fx; mz -= fz; }
    // Right-hand side of the heading, in the ground plane.
    if (held.has('d')) { mx += -fz; mz += fx; }
    if (held.has('a')) { mx -= -fz; mz -= fx; }
    const len = Math.hypot(mx, mz);
    if (deps.cameraRig.tileWalkaround()) {
      const fwd = (held.has('w') ? 1 : 0) - (held.has('s') ? 1 : 0);
      const right = (held.has('d') ? 1 : 0) - (held.has('a') ? 1 : 0);
      deps.cameraRig.tileWalk(fwd, right, 0.9 * sprint * Math.min(dt, 0.05));
    } else if (len > 0) {
      orbit.lookAt.x += (mx / len) * step;
      orbit.lookAt.z += (mz / len) * step;
      if (surfaceRegime() && deps.live.plot && !deps.roverDrive.state.driving) {
        // The step just moved the pivot's footprint, so re-floor it both
        // ways here (the per-frame ticks stay up-only for R/F and mesh
        // refinement) before checking for rocks at the new spot.
        deps.live.plot.standOnGround(orbit.lookAt, 0);
        // Out of the rocks in the same breath as the step, not on the next
        // frame's floor pass: the frame draws after this, so a step that lands
        // inside a boulder is a frame the player spends inside it.
        if (deps.live.plot.pushOutOfRocks(orbit.lookAt, WALK_BODY_R_M)) {
          deps.live.plot.standOnGround(orbit.lookAt, 0);
        }
      }
    }
    if (held.has('r')) orbit.lookAt.y += step;
    if (held.has('f')) orbit.lookAt.y -= step;
    stopCinema();
    deps.live.needsRender = true;
  }

  // ESC handling — close the atlas to T0 (caught in capture phase so the
  // menu's Esc handler doesn't run when atlas is mounted).
  function onKey(e: KeyboardEvent): void {
    if (deps.live.disposed || deps.worldHostState.externalWorld) return;
    // A text field owns its keystrokes: the site filter takes h, w, a, s and d
    // as letters, and Escape as "clear what I typed", not as flight controls.
    if (e.target instanceof HTMLInputElement && e.target.type === 'search') return;
    if (e.target instanceof HTMLSelectElement) return;
    // A focused slider owns only the keys a range input steps on, and Escape
    // while it has a sol or season preview to cancel, or this capture-phase
    // handler's `stopImmediatePropagation` would never let that release run.
    if (e.target instanceof HTMLInputElement && e.target.type === 'range'
      && (sliderHoldsEveryKey || RANGE_KEYS.has(e.key) || (e.key === 'Escape' && deps.sliderPreviewing))) return;
    if (e.key === 'Escape' && deps.siteChips.finderOpen) {
      deps.siteChips.closeFinder();
      document.getElementById('atlas-sf-head')?.focus();
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (e.key === 'Escape' && inspector.clearFlash()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      renderOverlay();
      return;
    }
    if (e.key === 'Escape' && state.tier > 0) {
      e.preventDefault();
      e.stopImmediatePropagation();
      // Escape steps out of the selection before it steps out of the tier — you
      // are far more likely to want the highlight gone than to want to leave the
      // plot, and leaving is one more press away.
      if (state.tier >= 5 && deps.live.selectedSub) {
        deps.live.selectedSub = null;
        applySelection();
        renderOverlay();
        return;
      }
      zoomOut();
      return;
    }
    const k = e.key.toLowerCase();
    if (k === 'h' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      setHexesEnabled(!deps.hexViewSeam.hexesEnabled);
      return;
    }
    if (state.tier >= 5 && (MOVE_KEYS.has(k) || k === 'shift')) {
      // Never swallow a browser or menu shortcut.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      held.add(k);
    }
  }
  function onKeyUp(e: KeyboardEvent): void {
    if (deps.worldHostState.externalWorld) return;
    if (e.target instanceof HTMLInputElement && e.target.type === 'search') return;
    if (e.target instanceof HTMLSelectElement) return;
    held.delete(e.key.toLowerCase());
  }
  // A key held while the window loses focus never sends its keyup, and the
  // camera would drift forever.
  function onBlur(): void { held.clear(); }
  window.addEventListener('keydown', onKey, true);
  window.addEventListener('keyup', onKeyUp, true);
  window.addEventListener('blur', onBlur);
  own(() => {
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('keyup', onKeyUp, true);
    window.removeEventListener('blur', onBlur);
  });

  return {
    applyMovement,
    get isDragging(): boolean { return isDragging; },
    held,
    moveInput,
    get dragInvertYaw(): boolean { return dragInvertYaw; },
    set dragInvertYaw(v: boolean) { dragInvertYaw = v; },
    get yawInertia(): number { return yawInertia; },
    set yawInertia(v: number) { yawInertia = v; },
    get pitchInertia(): number { return pitchInertia; },
    set pitchInertia(v: number) { pitchInertia = v; },
    INERTIA_DECAY,
  };
}
